import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppToast from "../../../shared/components/AppToast";
import { AgregarFamiliaDialog } from "../components/dialogs/AgregarFamilia";
import { EditarFamiliaDialog } from "../components/dialogs/EditarFamilia";
import { AtributoFormDialog } from "../components/dialogs/AtributoFormDialog";
import { EliminarAtributoDialog } from "../components/dialogs/EliminarAtributo";
import { EliminarValorAtributoDialog } from "../components/dialogs/EliminarValorAtributo";
import { ConfirmDialog as ConfirmDialogShared } from "../components/shared/ConfirmDialog";
import useFamiliaTree from "../hooks/useFamiliaTree";
import useFamiliaCrud from "../hooks/useFamiliaCrud";
import useAtributos from "../hooks/useAtributos";
import useValoresAtributo from "../hooks/useValoresAtributo";
import FamiliasTreePanel from "../components/familias/FamiliasTreePanel";
import FamiliaAtributosPanel from "../components/familias/FamiliaAtributosPanel";
import FamiliaValoresModal from "../components/familias/FamiliaValoresModal";
import useDialogState from "../hooks/useDialogState";

const Familias = () => {
    const navigate = useNavigate();
    const toastRef = useRef(null);
    const agregarFamiliaDialog = useDialogState();
    const editarFamiliaDialog = useDialogState();
    const [deleteConfirm, setDeleteConfirm] = useState({ visible: false, rowData: null });

    const showError = (msg, severity = "error") =>
        toastRef.current?.show({ severity, summary: severity === "warn" ? "No permitido" : "Error", detail: msg || "Ocurrió un error.", life: 4000 });

    const showToast = (cfg) => toastRef.current?.show({ life: 2500, ...cfg });

    const tree = useFamiliaTree({
        onError: () => showError("No fue posible cargar las familias."),
    });

    const crud = useFamiliaCrud({
        tipo: tree.tipo,
        selectedFamiliaData: tree.selectedFamiliaData,
        setSelectedFamiliaData: tree.setSelectedFamiliaData,
        reload: tree.reload,
        onError: (err) => showError(err?.message, err?.severity),
    });

    const atributos = useAtributos({
        selectedFamiliaData: tree.selectedFamiliaData,
        onError: () => showError("No fue posible completar la acción con los atributos."),
        onToast: showToast,
    });

    const valores = useValoresAtributo({
        atributo: atributos.valoresDialog.selectedItem,
        onError: () => showError("No fue posible completar la acción con los valores."),
        onToast: showToast,
    });

    const handleEliminar = (rowData) => {
        setDeleteConfirm({ visible: true, rowData });
    };

    const confirmEliminar = async () => {
        await crud.handleEliminarFamilia(deleteConfirm.rowData);
        setDeleteConfirm({ visible: false, rowData: null });
    };

    const handleEditar = (rowData) => {
        tree.setSelectedFamiliaData(rowData);
        editarFamiliaDialog.open(rowData);
    };

    const handleVerProductos = (rowData) => {
        const nombre = encodeURIComponent(rowData?.name || rowData?.nombre || "");
        navigate(`/administracion/familias/${rowData.id}/productos?tipo=${tree.tipo}&nombre=${nombre}`);
    };

    const handleEditarAtributo = (atributoData) => {
        atributos.editarDialog.open(atributoData);
    };

    const handleGuardarAtributo = (data) => {
        if (data?.id) {
            atributos.handleEditar(data);
        } else {
            atributos.handleAgregar(data);
        }
    };

    const handleEliminarAtributo = (atributoData) => {
        atributos.eliminarDialog.open(atributoData);
    };

    const handleGuardarValor = (data) => {
        if (data?.id) {
            return valores.handleEditar(data);
        } else {
            return valores.handleAgregar(data);
        }
    };

    const handleEliminarValor = (valorData) => {
        valores.eliminarDialog.open(valorData);
    };

    return (
        <>
            <div className="py-3" style={{ position: "relative" }}>
                
                <div className="grid">
                    <div className="col-12">
                        <h4 className="font-bold mb-1">Administración de Familias</h4>
                        <p className="text-700 text-lg line-height-3 mb-3">
                            Aqui puedes administrar las familias, sus atributos, prioridad de orden y gestión de productos.
                        </p>
                    </div>
                    
                    <FamiliasTreePanel
                        nodes={tree.nodes}
                        loading={tree.loading}
                        tipo={tree.tipo}
                        tipoOptions={tree.tipoOptions}
                        setTipo={tree.setTipo}
                        activas={tree.activas}
                        setActivas={tree.setActivas}
                        selectedFamilia={tree.selectedFamilia}
                        setSelectedFamilia={tree.setSelectedFamilia}
                        selectedFamiliaData={tree.selectedFamiliaData}
                        onNodeSelect={tree.onNodeSelect}
                        onNodeUnselect={tree.onNodeUnselect}
                        onAgregarClick={() => agregarFamiliaDialog.open()}
                        onSubirPrioridad={crud.handleSubirPrioridad}
                        onBajarPrioridad={crud.handleBajarPrioridad}
                        onToggleActivo={crud.handleToggleActivo}
                        onEliminar={handleEliminar}
                        onEditar={handleEditar}
                        onVerProductos={handleVerProductos}
                        actionLoading={crud.actionLoading}
                    />

                    <FamiliaAtributosPanel
                        selectedFamiliaData={tree.selectedFamiliaData}
                        atributos={atributos.atributos}
                        loadingAtributos={atributos.loading}
                        onAgregarAtributo={() => atributos.agregarDialog.open()}
                        onEditarAtributo={handleEditarAtributo}
                        onEliminarAtributo={handleEliminarAtributo}
                        onAbrirValores={(atributo) => atributos.valoresDialog.open(atributo)}
                    />
                </div>
            </div>

            <AgregarFamiliaDialog
                visible={agregarFamiliaDialog.visible}
                onCancel={agregarFamiliaDialog.close}
                onConfirm={crud.handleAgregarFamilia}
                familias={tree.familiasFlat}
                initialPadreId={tree.selectedFamiliaData?.id || 0}
            />
            <EditarFamiliaDialog
                visible={editarFamiliaDialog.visible}
                onCancel={editarFamiliaDialog.close}
                familia={editarFamiliaDialog.selectedItem}
                onConfirm={crud.handleEditarFamilia}
                loading={crud.actionLoading}
            />

            <AtributoFormDialog
                visible={atributos.agregarDialog.visible || atributos.editarDialog.visible}
                onCancel={() => {
                    atributos.agregarDialog.close();
                    atributos.editarDialog.close();
                }}
                familia={tree.selectedFamiliaData}
                atributo={atributos.editarDialog.selectedItem}
                onConfirm={handleGuardarAtributo}
            />
            <EliminarAtributoDialog
                visible={atributos.eliminarDialog.visible}
                onCancel={atributos.eliminarDialog.close}
                onConfirm={atributos.handleEliminar}
                atributo={atributos.eliminarDialog.selectedItem}
            />

            <FamiliaValoresModal
                visible={atributos.valoresDialog.visible}
                onHide={() => atributos.valoresDialog.close()}
                atributo={atributos.valoresDialog.selectedItem}
                valores={valores.valores}
                loadingValores={valores.loading}
                saving={valores.saving}
                onGuardarValor={handleGuardarValor}
                onEliminarValor={handleEliminarValor}
            />
            <EliminarValorAtributoDialog
                visible={valores.eliminarDialog.visible}
                onCancel={valores.eliminarDialog.close}
                onConfirm={valores.handleEliminar}
                valor={valores.eliminarDialog.selectedItem}
            />

            <AppToast ref={toastRef} />
            <ConfirmDialogShared
                visible={deleteConfirm.visible}
                onCancel={() => setDeleteConfirm({ visible: false, rowData: null })}
                onConfirm={confirmEliminar}
                header="Eliminar Familia"
                message={`¿Estás seguro de eliminar la familia "${deleteConfirm.rowData?.name || ""}"? Esta acción no se puede deshacer.`}
                loading={crud.actionLoading}
                acceptLabel="Eliminar"
                rejectLabel="Cancelar"
                acceptClassName="p-button-danger"
            />
        </>
    );
};

export default Familias;
